import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { SettingsData, SettingsForm } from '../../types';
import apiService from '../../services/api';

interface SettingsState {
  settings: SettingsData | null;
  loading: boolean;
  error: string | null;
}

const initialState: SettingsState = {
  settings: null,
  loading: false,
  error: null,
};

// Async thunks
export const fetchSettings = createAsyncThunk(
  'settings/fetchSettings',
  async () => {
    const response = await apiService.getSettings();
    return response;
  }
);

export const updateSettings = createAsyncThunk(
  'settings/updateSettings',
  async (settingsData: Partial<SettingsForm>) => {
    const response = await apiService.updateSettings(settingsData);
    return response;
  }
);

export const resetSettings = createAsyncThunk(
  'settings/resetSettings',
  async () => {
    const response = await apiService.resetSettings();
    return response;
  }
);

const settingsSlice = createSlice({
  name: 'settings',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Settings
      .addCase(fetchSettings.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSettings.fulfilled, (state, action) => {
        state.loading = false;
        state.settings = action.payload.settings;
      })
      .addCase(fetchSettings.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch settings';
      })
      // Update Settings
      .addCase(updateSettings.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateSettings.fulfilled, (state, action) => {
        state.loading = false;
        // apiService.updateSettings already unwraps response.data.settings
        state.settings = action.payload;
      })
      .addCase(updateSettings.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to update settings';
      })
      // Reset Settings
      .addCase(resetSettings.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(resetSettings.fulfilled, (state) => {
        state.loading = false;
        // Refetch settings after reset
        // This will be handled by the component
      })
      .addCase(resetSettings.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to reset settings';
      });
  },
});

export const { clearError } = settingsSlice.actions;

export default settingsSlice.reducer; 